import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./Button";

/**
 * TRY THE READ AGAIN. One control, one label, one busy state.
 *
 * ===================== EIGHTEEN CONTROLS, TWELVE LABELS ====================
 * The audit counted eight retry affordances with five labels (D10). The current
 * tree has eighteen with twelve: `try again` five times, plus `try once more`,
 * `reload review`, `retry my draft`, `refresh this level` twice, `read your
 * room again`, `check again`, `preview again`, `send again`, `start over` and
 * `send it again`. A facilitator cannot learn that those are one gesture.
 *
 * ===================== BUT THEY ARE NOT ALL ONE GESTURE ====================
 * Two groups are deliberately NOT collapsed into this control, and the
 * distinction is the reason the label is a prop with a default rather than a
 * constant:
 *
 *   SERVER-AUTHORED REFUSAL CONTROLS. `preview again`, `send again` and
 *   `start over` come from `REFUSAL_COPY` and `TOPOLOGY_REFUSAL_COPY`, where
 *   the client's only addition to the server's sentence is WHICH control to
 *   offer, and `start over` is emphatically not `send again` (one re-previews
 *   an idempotency conflict, the other resends a contended commit). Earned
 *   divergence 10 protects the sentences; these controls carry the same weight.
 *   They keep their own `RefusalControl` wrappers on their own surfaces, which
 *   reach the same secondary `Button` shape this component does, and they pass
 *   their own label. They are aligned with this control, not routed through it.
 *
 *   RE-RUNS THAT ARE NOT RETRIES. `preview this plan again` after an edit and
 *   `check this again` after a correction re-run a SUCCESSFUL read against
 *   changed input. Calling those retry would tell a facilitator something
 *   failed. They keep their own controls and are out of this component's scope.
 *
 * What is collapsed is the ordinary case: a read failed, nothing is lost, press
 * this. That is `try again`, and it is the default.
 *
 * ===================== THE BUSY STATE IS THE POINT =========================
 * Of the eighteen, exactly ONE had any busy treatment. Every read-retry in the
 * OS was a button that could be pressed five times into a slow network with no
 * acknowledgement, which is both a stack of duplicate requests and, for the
 * facilitator, no evidence the first press registered. The control latches the
 * value returned by `onRetry` until it settles, and `busy` covers work owned by
 * a caller that began elsewhere. Either route uses `Button`'s `loading`, so the
 * control gains a spinner AND stops accepting clicks while keeping keyboard
 * focus (`Button` uses `aria-disabled` rather than the native attribute
 * precisely so focus is not dropped to `<body>` mid-action).
 *
 * ===================== PLACEMENT IS ALSO STANDARDISED ======================
 * `mt-3` below the sentence that explains the failure, because a retry with no
 * explanation above it is a button that asks the facilitator to guess. The four
 * stacked sites shipped `mt-4`, `mt-4`, `mt-5` and `mt-3`; one value now, and
 * it is the tightest of the four because the sentence above it is the control's
 * own explanation rather than a neighbouring paragraph.
 *
 * Callers that sit INLINE (the context bar strip, a refusal row) pass `inline`
 * and get no top margin and the small size, because a strip that is always on
 * screen cannot afford a full-height control. Nothing else about position is
 * offered, and there is deliberately no `className` escape hatch: two callers
 * passing competing margin utilities is how the four values above happened.
 *
 * ===================== SECONDARY, NEVER PRIMARY ============================
 * One site shipped a filled primary retry (Review's error boundary, `reload
 * review`). A retry is not a page's primary action even on a page that has
 * failed: the brief allows one primary per state, and on an error surface the
 * teacher's real next move is often to leave. Secondary everywhere.
 */
export const RetryControl = ({
  onRetry,
  busy = false,
  label = "try again",
  inline = false,
  testId,
}) => {
  const [retryPending, setRetryPending] = useState(false);
  const retryPendingRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const retry = useCallback(
    (event) => {
      if (busy || retryPendingRef.current) return;
      retryPendingRef.current = true;

      let result;
      try {
        result = onRetry?.(event);
      } catch (error) {
        retryPendingRef.current = false;
        throw error;
      }

      const release = () => {
        retryPendingRef.current = false;
        if (mountedRef.current) setRetryPending(false);
      };
      if (result && typeof result.then === "function") {
        setRetryPending(true);
        Promise.resolve(result).then(release, release);
      } else {
        Promise.resolve().then(() => {
          retryPendingRef.current = false;
        });
      }
    },
    [busy, onRetry],
  );

  return (
    <Button
      variant="secondary"
      size={inline ? "sm" : "md"}
      loading={busy || retryPending}
      onClick={retry}
      data-testid={testId}
      className={inline ? "" : "mt-3"}
    >
      {label}
    </Button>
  );
};

export default RetryControl;
